// Client weather: the render half of the global weather system (the sim lives
// in src/server/weather.ts; ONE state world-wide, delivered as change-driven
// `weather` broadcasts — see the Weather section in shared.ts).
//
// This module mirrors the server's target + ramp and eases the RENDERED values
// toward it locally (same smoothstep the server uses), so the wire is silent
// between changes. Visuals (fog/light grading, clouds, precipitation, moon,
// lightning) hang off `updateWeather`, which game.ts calls immediately AFTER
// `dayNight.update` each frame — that ordering is load-bearing: daynight writes
// absolute light/sky values every frame and weather grades on top of them.
//
// CLIENT-ONLY: never imported by index.ts / src/server/**. Any DOM use (canvas
// texture bakes) stays inside functions (the label.ts rule).

import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { smoothstep, FOG_NEAR, FOG_FAR, type DayNight } from "./daynight.ts";
import {
  shelterBoxesNear, playerShelter, indoorWeight,
  INDOOR_FOG_NEAR, INDOOR_FOG_FAR, INDOOR_SKY,
} from "./cutaway.ts";
import { makeToon } from "./toon.ts";
import { heightAt } from "./terrain.ts";
import {
  DEFAULT_WEATHER, easeWeatherTarget, moonPhase, isColdSeason,
  type WeatherTarget,
} from "./shared.ts";
import { setWeatherAmbience } from "./audio.ts";

// Rain audio crossfade + duck knobs (client-only presentation). Driven from
// rendered rainAmt = kind===rain ? effPrecip() : 0 so audio matches particles.
const RAIN_AUDIO = {
  lightOutStart: 0.25, // light starts yielding to heavy past this rainAmt
  lightOutEnd: 0.85,
  heavyInStart: 0.2,
  heavyInEnd: 0.9,
  stormLightBoost: 0.15,
  stormHeavyBoost: 0.45,
  duckStart: 0.35,      // map ambience begins ducking
  duckMax: 0.55,        // duck fraction at full rain
  duckStormExtra: 0.35, // extra duck at full storm
};

// ---- Mirrored state ----------------------------------------------------------

const cur: WeatherTarget = { ...DEFAULT_WEATHER }; // rendered values (eased)
let from: WeatherTarget = { ...DEFAULT_WEATHER };
let tgt: WeatherTarget = { ...DEFAULT_WEATHER };
let rampS = 0;   // transition length (seconds; 0 = snap)
let rampT = 0;   // elapsed
let day = 0;     // world-day counter → moonPhase / isColdSeason
let frozen = false; // server-side drift freeze (dev) — display only

/** A `weather` message landed: re-seed the ease from wherever the rendered
 *  values currently sit toward the new target. Join baselines arrive ramp:0. */
export function applyWeatherMsg(m: {
  tgt: WeatherTarget; ramp: number; day: number; frozen?: boolean;
}): void {
  from = { ...cur };
  tgt = { ...m.tgt };
  rampS = Math.max(0, Number(m.ramp) || 0);
  rampT = 0;
  day = Math.max(0, Math.floor(Number(m.day) || 0));
  frozen = !!m.frozen;
  if (rampS === 0) easeWeatherTarget(cur, from, tgt, 1); // snap
}

/** Ease the rendered values toward the target (the SAME shared helper the
 *  server sim steps with, so the two sides can't drift). */
function stepEase(dt: number): void {
  if (rampT >= rampS) return;
  rampT = Math.min(rampS, rampT + dt);
  easeWeatherTarget(cur, from, tgt, rampT / rampS);
}

// ---- Effective (mixed) values -------------------------------------------------
// The mixing rules, client half: precipitation/storm only read at full strength
// under a closed sky, so a mid-ramp frame fades rain in as the clouds close
// over instead of raining from a half-clear sky.

/** Precipitation as rendered (gated on cloud cover). */
export function effPrecip(): number { return cur.precip * smoothstep(0.5, 0.7, cur.cloud); }
/** Storm as rendered (gated on heavy cloud). */
export function effStorm(): number { return cur.storm * smoothstep(0.75, 0.9, cur.cloud); }
/** One 0..1 "how grim is the sky" scalar — drives all light/fog grading. */
export function gloom(): number {
  return Math.min(1, 0.45 * cur.cloud + 0.45 * effPrecip() + 0.35 * effStorm());
}

// ---- Read access ---------------------------------------------------------------

/** The rendered weather values (eased; read-only by convention). */
export function weatherNow(): Readonly<WeatherTarget> { return cur; }

/** Snap to clear skies (map swap into an `env.weather:false` map — plan 24).
 *  The server never sends weather INTO such maps (broadcast.ts skips them), so
 *  whatever was falling on the old map would keep falling here without this.
 *  Also zeros rain beds immediately so the ambience mix starts fading without
 *  waiting for the next updateWeather tick. The world-day counter survives —
 *  the moon/season aren't weather. */
export function weatherClear(): void {
  applyWeatherMsg({ tgt: { ...DEFAULT_WEATHER }, ramp: 0, day, frozen });
  setWeatherAmbience({ light: 0, heavy: 0, ambienceDuck: 0 });
}

/** Everything the F4 dev panel shows (see weatherdev.ts). */
export function weatherDebugState(): {
  cur: WeatherTarget; tgt: WeatherTarget; rampLeft: number;
  day: number; phase: number; cold: boolean; frozen: boolean;
} {
  return {
    cur: { ...cur }, tgt: { ...tgt }, rampLeft: Math.max(0, rampS - rampT),
    day, phase: moonPhase(day), cold: isColdSeason(day), frozen,
  };
}

// ---- Visual systems ------------------------------------------------------------

let scene: THREE.Scene | null = null;
let dn: DayNight | null = null;
const hemiGround0 = new THREE.Color(); // hemi ground base, captured at init —
// weather re-derives groundColor from it each frame (daynight never writes it,
// so lerping in place would accumulate frame over frame).

// Grading palette + fog envelope. The clear baseline (FOG_NEAR/FOG_FAR) is
// imported from daynight.ts — grading only ever SHRINKS toward the heavy
// bounds, never past far=135 (the CAM_FAR + IM nearRadius contracts).
const GLOOM_SKY = new THREE.Color(0x8a95a3); // cold slate overcast
const SNOW_SKY = new THREE.Color(0xb9c2cc);  // brighter, whiter snow sky
const WHITE = new THREE.Color(0xffffff);
const FOG_NEAR_HEAVY = 16, FOG_FAR_HEAVY = 55;

// Moon: a phase-textured sprite riding opposite the sun, and a small ambient
// bonus/penalty by phase (full moon brightens the night, new moon darkens).
const MOON_DIST = 120;  // from the player — inside CAM_FAR at any camera offset
const MOON_SCALE = 9;
const MOON_AMBIENT = [-0.04, -0.02, 0, 0.03, 0.06, 0.03, 0, -0.02];
let moon: THREE.Sprite | null = null;
const moonTex: (THREE.CanvasTexture | null)[] = new Array(8).fill(null);
const _dir = new THREE.Vector3();

export function initWeather(sc: THREE.Scene, dayNight: DayNight): void {
  scene = sc;
  dn = dayNight;
  hemiGround0.copy(dn.hemi.groundColor);
  moon = new THREE.Sprite(new THREE.SpriteMaterial({
    map: moonTexture(4), transparent: true, opacity: 0,
    fog: false, depthWrite: false,
  }));
  moon.scale.setScalar(MOON_SCALE);
  moon.visible = false;
  sc.add(moon);
  initPrecip(sc);
  initClouds(sc);
}

/** Canvas-baked phase texture (lazy, one per phase). DOM stays inside the
 *  function — the label.ts rule. Classic two-circle mask: a same-size shadow
 *  disc punched out at an offset that grows with the lit fraction; phase 0
 *  (new) bakes fully transparent (the ambient penalty carries "no moon"). */
function moonTexture(phase: number): THREE.CanvasTexture {
  const cached = moonTex[phase];
  if (cached) return cached;
  const S = 128, r = 46, c = S / 2;
  const cv = document.createElement("canvas");
  cv.width = cv.height = S;
  const g = cv.getContext("2d")!;
  g.fillStyle = "#f2eedd";
  g.beginPath(); g.arc(c, c, r, 0, Math.PI * 2); g.fill();
  // Two faint maria blotches so the disc reads "moon", not "dot".
  g.fillStyle = "rgba(0,0,0,0.08)";
  g.beginPath(); g.arc(c - 14, c - 8, 9, 0, Math.PI * 2); g.fill();
  g.beginPath(); g.arc(c + 10, c + 14, 6, 0, Math.PI * 2); g.fill();
  if (phase !== 4) {
    const lit = phase < 4 ? phase / 4 : (8 - phase) / 4; // 0 new … 1 full
    const side = phase < 4 ? 1 : -1; // waxing lights the right edge first
    g.globalCompositeOperation = "destination-out";
    g.beginPath(); g.arc(c - side * 2 * r * lit, c, r + 1, 0, Math.PI * 2); g.fill();
    g.globalCompositeOperation = "source-over";
  }
  const tex = new THREE.CanvasTexture(cv);
  moonTex[phase] = tex;
  return tex;
}

// Edge-of-the-world haze (src/edge/preview.ts): 0..1 fog thickening pushed per
// frame as the player nears an UNLINKED map edge, so the boundary dissolves
// into mist instead of hard-cutting to sky. Folded into `heavy` below — it
// composes with precip/storm via max() and only ever SHRINKS fog from the
// baseline, so the CAM_FAR / interest-management far-fog contracts hold.
let edgeHaze = 0;
export function setEdgeHaze(v: number): void {
  edgeHaze = Math.min(1, Math.max(0, v));
}

/** The atmosphere grade: runs strictly AFTER dayNight.update each frame (that
 *  ordering is load-bearing — daynight writes ABSOLUTE light intensities and
 *  sky color, so multiplying/lerping here composes without accumulation), and
 *  OWNS fog.near/far (daynight's constructor values are the clear baseline). */
function gradeAtmosphere(night: number): void {
  if (!scene || !dn) return;
  const g = gloom();
  const precip = effPrecip();
  const heavy = Math.max(precip, effStorm(), edgeHaze);
  const snowing = cur.kind === "snow" && precip > 0;
  const sky = scene.background as THREE.Color;
  const fog = scene.fog as THREE.Fog;
  // Sky first, then re-slave fog color to it (daynight already matched them —
  // keep that identity so the horizon melts into the fog wall seamlessly).
  sky.lerp(snowing ? SNOW_SKY : GLOOM_SKY, g * 0.8);
  fog.near = FOG_NEAR + (FOG_NEAR_HEAVY - FOG_NEAR) * heavy;
  fog.far = FOG_FAR + (FOG_FAR_HEAVY - FOG_FAR) * heavy;
  // Light grade: overcast kills direct sun (its shadow fades with intensity);
  // fills dim less so the world darkens without going flat black.
  dn.sun.intensity *= 1 - 0.8 * g;
  dn.hemi.intensity *= 1 - 0.35 * g;
  dn.ambient.intensity *= 1 - 0.25 * g;
  // Tint-level snow dressing: the ground-bounce light whitens as snow falls.
  dn.hemi.groundColor.copy(hemiGround0);
  if (snowing) dn.hemi.groundColor.lerp(WHITE, precip * 0.6);
  // Moonlight: phase-shaped ambient bonus, only at night under an open sky.
  dn.ambient.intensity = Math.max(
    0, dn.ambient.intensity + MOON_AMBIENT[moonPhase(day)] * night * (1 - cur.cloud),
  );
  // Lightning flash: spike the ambient + whiten the sky for the envelope's few
  // frames — bright enough to cross the bloom threshold, so the composer's
  // UnrealBloomPass flares the whole sky for free.
  if (flashT > 0) {
    const env = flashT / FLASH_DUR;
    dn.ambient.intensity += 2.2 * env;
    sky.lerp(WHITE, 0.55 * env);
  }
  fog.color.copy(sky);
}

// ---- Indoor blackout ------------------------------------------------------------
// Exterior void while the player is sheltered (same predicate as cutaway). Runs
// AFTER updateCutaway in the frame so indoorWeight() is current — weather's
// outdoor grade above already ran; we only darken/shrink further. Outdoor
// geometry stays (doorway/window outdoor glimpses); outdoor entities are hidden
// separately via hideOutdoorWhileIndoor. Purely client-visual.
const _indoorBlack = new THREE.Color(INDOOR_SKY);

// Clouds ship at opacity 0.92 (initClouds) — indoor grade multiplies that base
// so outdoor re-entry always restores the same look.
const CLOUD_BASE_OPACITY = 0.92;

/** Grade sky/fog/lights toward a black outdoor void while indoors. Call after
 *  updateCutaway each frame (weather already wrote the outdoor atmosphere). */
export function gradeIndoorBlackout(): void {
  if (!scene || !dn) return;
  const w = indoorWeight();
  // Always restore cloud opacity when outdoors — a previous indoor frame may
  // have dimmed them, and we early-out of the rest of the grade at w=0.
  if (cloudMat) cloudMat.opacity = CLOUD_BASE_OPACITY * (1 - w);
  if (clouds && !clouds.visible) clouds.visible = true;
  if (w <= 0) return;
  const sky = scene.background as THREE.Color;
  const fog = scene.fog as THREE.Fog;
  sky.lerp(_indoorBlack, w);
  // Shrink fog only (never expand past the outdoor grade weather just wrote).
  fog.near += (Math.min(fog.near, INDOOR_FOG_NEAR) - fog.near) * w;
  fog.far += (Math.min(fog.far, INDOOR_FOG_FAR) - fog.far) * w;
  if (fog.near >= fog.far - 0.5) fog.near = fog.far * 0.4;
  fog.color.copy(sky);
  // Nudge outdoor sun/hemi down just enough to read as "indoors" — the interior
  // should sit only slightly darker than the world outside, not plunge to a
  // near-black void. (The black sky/fog above already hides the exterior.)
  dn.sun.intensity *= 1 - 0.2 * w;
  dn.hemi.intensity *= 1 - 0.12 * w;
  if (clouds) clouds.visible = w < 0.98;
}

// ---- Precipitation --------------------------------------------------------------
// Two GPU-animated primitives in a wrap-around box centered on the camera —
// geometry is built ONCE, every particle's motion lives in the vertex shader
// off a time uniform (the instanced-sway precedent: zero per-particle CPU,
// ever). Intensity is a drawRange + opacity knob; `kind` picks which system
// draws. The volume (≤ ~30u from the camera) sits under even the heavy-weather
// fog wall, so the materials skip fog.
//
// ROOF CLIPPING: both shaders test each particle against the nearby structures'
// roofed volumes (`shelterBoxesNear`, cutaway.ts — footprint AABB up to the
// roof, or up to the top walkable floor when open-topped like the castle
// rampart) and zero its alpha inside one — so buildings seen from OUTSIDE don't
// leak rain through their roofs, and open rooftops still catch weather. All in
// the vertex shader, no per-particle CPU; the box list refreshes as the player
// moves. While the player is INSIDE one, that structure's box GROWS (see
// applyRoofUniforms) so the interior view stays dry while the storm keeps
// falling outside the doorway and past the windows.

const RAIN_MAX = 4000;  // drops (2 verts each) at full downpour
const SNOW_MAX = 2600;  // flakes at full blizzard
const PRECIP_XZ = 18;   // half-extent of the box around the player
const PRECIP_H = 28;    // box height (wraps in y)
const PRECIP_SINK = 3;  // box bottom sits this far below the player's feet
const RAIN_FALL = 22;   // units/sec
const SNOW_FALL = 1.6;
const ROOF_MAX = 6;     // shader clip budget: nearest roofed volumes (uniform array)
const ROOF_REFRESH = 8; // re-query shelter boxes after the player moves this far

let rain: THREE.LineSegments | null = null;
let snow: THREE.Points | null = null;
const uTime = { value: 0 };
const uCenter = { value: new THREE.Vector3() };
const uWind = { value: 0 };
const uRainOpacity = { value: 0 };
const uSnowOpacity = { value: 0 };
// Roof clip boxes: xz = (minX, minZ, maxX, maxZ), y = clip top. Unused slots
// hold an inside-out box (minX > maxX) that can never match, so the shader
// loop needs no count uniform (fixed bound keeps GLSL ES 1.0 happy).
const EMPTY_BOX = new THREE.Vector4(1e9, 1e9, -1e9, -1e9);
const uRoofXZ = {
  value: Array.from({ length: ROOF_MAX }, () => EMPTY_BOX.clone()),
};
const uRoofY = { value: new Float32Array(ROOF_MAX) };

// GLSL shared by both precip vertex shaders: 1.0 in the open, 0.0 under a roof.
const ROOF_GLSL = /* glsl */ `
  uniform vec4 uRoofXZ[${ROOF_MAX}]; uniform float uRoofY[${ROOF_MAX}];
  float roofClip(vec3 wp) {
    for (int i = 0; i < ${ROOF_MAX}; i++) {
      vec4 b = uRoofXZ[i];
      if (wp.x > b.x && wp.z > b.y && wp.x < b.z && wp.z < b.w && wp.y < uRoofY[i]) return 0.0;
    }
    return 1.0;
  }`;

function initPrecip(sc: THREE.Scene): void {
  // Shared seed layout: per-particle origin jittered through the box; the
  // shader wraps y downward over time so the volume never empties or pops.
  const seedBox = (out: THREE.Vector3): THREE.Vector3 => out.set(
    (Math.random() * 2 - 1) * PRECIP_XZ,
    Math.random() * PRECIP_H,
    (Math.random() * 2 - 1) * PRECIP_XZ,
  );
  const v = new THREE.Vector3();

  // Rain: LineSegments (Points can't render streaks; 2-vert segments can, in
  // one draw call). Both verts of a drop share its seed; `tip` (0 top, 1
  // bottom) stretches the streak along the wind-slanted fall direction.
  {
    const seeds = new Float32Array(RAIN_MAX * 2 * 3);
    const tips = new Float32Array(RAIN_MAX * 2);
    const pos = new Float32Array(RAIN_MAX * 2 * 3); // required attr; shader ignores it
    for (let i = 0; i < RAIN_MAX; i++) {
      seedBox(v);
      seeds.set([v.x, v.y, v.z, v.x, v.y, v.z], i * 6);
      tips[i * 2] = 0; tips[i * 2 + 1] = 1;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    geo.setAttribute("seed", new THREE.BufferAttribute(seeds, 3));
    geo.setAttribute("tip", new THREE.BufferAttribute(tips, 1));
    const mat = new THREE.ShaderMaterial({
      transparent: true, depthWrite: false,
      uniforms: { uTime, uCenter, uWind, uOpacity: uRainOpacity, uRoofXZ, uRoofY },
      vertexShader: /* glsl */ `
        uniform float uTime; uniform vec3 uCenter; uniform float uWind;
        attribute vec3 seed; attribute float tip;
        varying float vClip;
        ${ROOF_GLSL}
        void main() {
          // Wrap-fall through the box; uCenter is the PLAYER'S FEET, and the
          // span starts PRECIP_SINK below them — anchoring to the camera put
          // the whole volume above the eye (the camera looks down at the
          // player from ~14u up, so an upward-only box left the frustum).
          float y = mod(seed.y - uTime * ${RAIN_FALL.toFixed(1)}, ${PRECIP_H.toFixed(1)}) - ${PRECIP_SINK.toFixed(1)};
          vec3 p = vec3(seed.x, y, seed.z);
          // Streak: the bottom vert trails 0.55u along the slanted fall dir.
          vec3 fall = normalize(vec3(uWind * 0.6, -1.0, 0.0));
          p += fall * 0.55 * tip;
          vec3 wp = p + uCenter;
          vClip = roofClip(wp);
          gl_Position = projectionMatrix * viewMatrix * vec4(wp, 1.0);
        }`,
      fragmentShader: /* glsl */ `
        uniform float uOpacity; varying float vClip;
        void main() {
          if (vClip < 0.01) discard;
          gl_FragColor = vec4(0.68, 0.75, 0.83, uOpacity * vClip);
        }`,
    });
    rain = new THREE.LineSegments(geo, mat);
    rain.frustumCulled = false; // wraps around the camera — always in view
    rain.visible = false;
    sc.add(rain);
  }

  // Snow: Points with a soft round alpha mask from gl_PointCoord (no texture
  // asset) — slow fall + a sinusoidal per-flake drift, all in-shader.
  {
    const seeds = new Float32Array(SNOW_MAX * 3);
    const rnds = new Float32Array(SNOW_MAX);
    const pos = new Float32Array(SNOW_MAX * 3);
    for (let i = 0; i < SNOW_MAX; i++) {
      seedBox(v);
      seeds.set([v.x, v.y, v.z], i * 3);
      rnds[i] = Math.random();
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    geo.setAttribute("seed", new THREE.BufferAttribute(seeds, 3));
    geo.setAttribute("rnd", new THREE.BufferAttribute(rnds, 1));
    const mat = new THREE.ShaderMaterial({
      transparent: true, depthWrite: false,
      uniforms: { uTime, uCenter, uWind, uOpacity: uSnowOpacity, uRoofXZ, uRoofY },
      vertexShader: /* glsl */ `
        uniform float uTime; uniform vec3 uCenter; uniform float uWind;
        attribute vec3 seed; attribute float rnd;
        varying float vClip;
        ${ROOF_GLSL}
        void main() {
          // Same player-feet anchor + below-feet sink as the rain shader.
          float y = mod(seed.y - uTime * (${SNOW_FALL.toFixed(1)} + rnd * 0.8), ${PRECIP_H.toFixed(1)}) - ${PRECIP_SINK.toFixed(1)};
          float x = seed.x + sin(uTime * 0.7 + rnd * 6.2832) * (0.4 + uWind * 1.2);
          vec3 wp = vec3(x, y, seed.z) + uCenter;
          vClip = roofClip(wp);
          vec4 mv = viewMatrix * vec4(wp, 1.0);
          gl_PointSize = clamp((3.0 + rnd * 3.0) * (24.0 / -mv.z), 2.0, 8.0);
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: /* glsl */ `
        uniform float uOpacity; varying float vClip;
        void main() {
          float a = smoothstep(0.5, 0.22, length(gl_PointCoord - 0.5)) * uOpacity * vClip;
          if (a < 0.01) discard;
          gl_FragColor = vec4(0.96, 0.97, 1.0, a);
        }`,
    });
    snow = new THREE.Points(geo, mat);
    snow.frustumCulled = false;
    snow.visible = false;
    sc.add(snow);
  }
}

// Re-query the nearby roof boxes when the player has strayed far enough that
// the cached set may no longer cover the precip volume. Query radius = volume
// half-extent + the refresh hysteresis, so a roof can't slip inside the volume
// between refreshes. (The uniforms themselves are written per frame below.)
let roofX = Infinity, roofZ = Infinity;
let roofBoxes: ReturnType<typeof shelterBoxesNear> = [];
function refreshRoofs(player: THREE.Vector3): void {
  if (
    Math.abs(player.x - roofX) < ROOF_REFRESH &&
    Math.abs(player.z - roofZ) < ROOF_REFRESH
  ) return;
  roofX = player.x; roofZ = player.z;
  roofBoxes = shelterBoxesNear(player.x, player.z, PRECIP_XZ + ROOF_REFRESH, ROOF_MAX);
}

// While the player is sheltered inside a structure, the roofline clip alone
// can't keep the interior VIEW dry: the camera orbits OUTSIDE the walls, so
// legal outdoor drops between it and the cutaway-faded near wall (opacity < 1
// ⇒ no depth write) draw superimposed over the room — and so does rain showing
// through the cut-away ceiling overhead (in the castle, everything above the
// open rampart deck). So the SHELTERING structure's box (matched by footprint;
// `playerShelter` is null on an open rooftop, so you still get rained on up
// there) grows two ways: horizontally to also cover the camera (killing the
// camera→wall corridor; the overshoot is behind the camera where nothing is
// visible) and vertically to a full column (no drops through the opened
// ceiling). Rain elsewhere keeps falling — out the doorway, past the windows,
// beyond the far walls (which still write depth and occlude correctly).
const CAM_PAD = 3; // horizontal slack around the camera position
function applyRoofUniforms(cam: THREE.Vector3): void {
  const sh = playerShelter(); // last cutaway pass (runs after us) — 1 frame stale
  let matched = false;
  for (let i = 0; i < ROOF_MAX; i++) {
    const b = roofBoxes[i];
    if (!b) { uRoofXZ.value[i]!.copy(EMPTY_BOX); continue; }
    if (sh && b.minX === sh.minX && b.minZ === sh.minZ) {
      matched = true;
      uRoofXZ.value[i]!.set(
        Math.min(b.minX, cam.x - CAM_PAD), Math.min(b.minZ, cam.z - CAM_PAD),
        Math.max(b.maxX, cam.x + CAM_PAD), Math.max(b.maxZ, cam.z + CAM_PAD),
      );
      uRoofY.value[i] = 1e9;
    } else {
      uRoofXZ.value[i]!.set(b.minX, b.minZ, b.maxX, b.maxZ);
      uRoofY.value[i] = b.yTop;
    }
  }
  // Sheltering structure fell off the nearest-6 budget (would take >6 roofed
  // volumes inside a 26u radius) — claim the last slot so indoors always wins.
  if (sh && !matched) {
    uRoofXZ.value[ROOF_MAX - 1]!.set(
      Math.min(sh.minX, cam.x - CAM_PAD), Math.min(sh.minZ, cam.z - CAM_PAD),
      Math.max(sh.maxX, cam.x + CAM_PAD), Math.max(sh.maxZ, cam.z + CAM_PAD),
    );
    uRoofY.value[ROOF_MAX - 1] = 1e9;
  }
}

/** Scale the live systems to the rendered intensity: particle COUNT via
 *  drawRange (the cheap knob), opacity easing in the drizzle-end so light
 *  precipitation is a few faint streaks rather than full-count-at-low-alpha.
 *  The volume anchors to the PLAYER'S FEET (not the camera — see the shader
 *  comment): the box spans PRECIP_SINK below them to fill the whole view. */
function updatePrecip(t: number, player: THREE.Vector3, cam: THREE.Vector3): void {
  if (!rain || !snow) return;
  uTime.value = t;
  uCenter.value.copy(player);
  uWind.value = cur.wind;
  refreshRoofs(player);
  applyRoofUniforms(cam);
  const p = effPrecip();
  const rainAmt = cur.kind === "rain" ? p : 0;
  const snowAmt = cur.kind === "snow" ? p : 0;
  const nRain = Math.floor(rainAmt * RAIN_MAX);
  rain.visible = nRain > 0;
  rain.geometry.setDrawRange(0, nRain * 2);
  uRainOpacity.value = 0.18 + 0.32 * rainAmt;
  const nSnow = Math.floor(snowAmt * SNOW_MAX);
  snow.visible = nSnow > 0;
  snow.geometry.setDrawRange(0, nSnow);
  uSnowOpacity.value = 0.6 + 0.35 * snowAmt;
}

// ---- Cloud layer -----------------------------------------------------------------
// Sky tint alone can't show "partly cloudy", so cloud cover is a real layer:
// ONE InstancedMesh of a merged squashed-sphere puff (flat-shaded toon white,
// fogged so horizon clouds melt into the fog wall). Placements jitter a logical
// tile that WRAPS around the player as they move and as wind drifts it — a
// cloud exiting one edge re-enters the other already deep in fog, so no pops.
// Coverage = the instanced draw count (the cheapest intensity knob). ≤40
// instances / 1 draw call / no shadows (the sun-dimming grade already reads as
// cloud shadow world-wide; instance shadows would cost and look wrong).

const CLOUD_MAX = 40;
const CLOUD_TILE = 300; // logical wrap tile side (player-centered)
const CLOUD_Y = 78;
let clouds: THREE.InstancedMesh | null = null;
let cloudMat: THREE.MeshToonMaterial | null = null;
// Per-instance home spot in the tile + shape jitter (built once; the yaw is
// baked straight into a quaternion since it never changes after init).
const cloudHome: { x: number; z: number; y: number; s: number; q: THREE.Quaternion }[] = [];
let cloudDrift = 0; // accumulated wind travel along +x
const _m4 = new THREE.Matrix4();
const _s = new THREE.Vector3();
const _p = new THREE.Vector3();
const _axisY = new THREE.Vector3(0, 1, 0);

function initClouds(sc: THREE.Scene): void {
  // One puff: 4 squashed spheres merged (low-poly — the toon ramp does the rest).
  const parts: THREE.BufferGeometry[] = [];
  const lobe = (x: number, z: number, r: number) => {
    const g = new THREE.SphereGeometry(r, 7, 5);
    g.scale(1, 0.45, 1);
    g.translate(x, 0, z);
    parts.push(g);
  };
  lobe(0, 0, 1);
  lobe(0.9, 0.15, 0.7);
  lobe(-0.85, -0.1, 0.65);
  lobe(0.15, -0.7, 0.6);
  const puff = mergeGeometries(parts, false)!;
  for (const g of parts) g.dispose();
  // No depth write (plan 23): clouds in the depth buffer would get inked by the
  // silhouette-outline pass against the sky on every puff. The implicit contract
  // for the whole atmosphere layer — rain/snow/moon are already depthWrite:false.
  cloudMat = makeToon(
    { color: 0xffffff, transparent: true, opacity: 0.92, depthWrite: false },
    { rim: 0.3 }, // gentle — clouds are atmosphere, not a character read
  );
  clouds = new THREE.InstancedMesh(puff, cloudMat, CLOUD_MAX);
  clouds.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  clouds.castShadow = false;
  clouds.receiveShadow = false;
  clouds.frustumCulled = false; // wraps around the player — always overhead
  // Without depthWrite, moon-vs-cloud layering falls back to transparent sort
  // order — and this InstancedMesh sorts by its ORIGIN (0,0,0), not the puffs
  // overhead. Force clouds after the moon sprite: it now glows faintly through
  // thin cloud instead of being hard z-killed.
  clouds.renderOrder = 1;
  clouds.count = 0;
  sc.add(clouds);
  for (let i = 0; i < CLOUD_MAX; i++) {
    cloudHome.push({
      x: (Math.random() - 0.5) * CLOUD_TILE,
      z: (Math.random() - 0.5) * CLOUD_TILE,
      y: CLOUD_Y + (Math.random() - 0.5) * 14,
      s: 8 + Math.random() * 8,
      q: new THREE.Quaternion().setFromAxisAngle(_axisY, Math.random() * Math.PI * 2),
    });
  }
}

const wrapTile = (v: number): number =>
  ((v % CLOUD_TILE) + CLOUD_TILE * 1.5) % CLOUD_TILE - CLOUD_TILE / 2;

function updateClouds(dt: number, player: THREE.Vector3): void {
  if (!clouds || !cloudMat) return;
  const n = Math.round(cur.cloud * CLOUD_MAX);
  clouds.count = n;
  if (n === 0) return;
  cloudDrift += dt * (0.5 + cur.wind * 3.0);
  // Storm-dark clouds: one material tint for the whole layer (cheaper than
  // per-instance colors and reads fine — variation comes from scale/height).
  const dark = Math.max(effStorm(), gloom() * 0.5);
  cloudMat.color.setScalar(1 - 0.45 * dark);
  for (let i = 0; i < n; i++) {
    const h = cloudHome[i];
    _p.set(
      player.x + wrapTile(h.x + cloudDrift - player.x),
      h.y,
      player.z + wrapTile(h.z - player.z),
    );
    _s.setScalar(h.s);
    clouds.setMatrixAt(i, _m4.compose(_p, h.q, _s));
  }
  clouds.instanceMatrix.needsUpdate = true;
}

// ---- Lightning --------------------------------------------------------------------
// Client-local (zero wire — cosmetic, nobody can compare flash timing across
// screens; flagged for a server-side broadcastNear move if bolts ever gain
// gameplay). A flash is a 0.12s envelope read by gradeAtmosphere: it spikes
// the ambient light + whitens the sky past the bloom threshold (0.82), so the
// existing UnrealBloomPass flares it for FREE — no new lights (MAX_LIGHTS
// budget untouched). ~30% of flashes drop a transient jagged bolt mesh in the
// middle distance (projectile.ts-style lifecycle, ≤2 alive).

const FLASH_DUR = 0.12;
const BOLT_LIFE = 0.18;
let flashT = 0;     // remaining flash envelope (seconds)
let nextFlash = 0;  // countdown to the next flash while storming
const bolts: { group: THREE.Group; mat: THREE.MeshBasicMaterial; life: number }[] = [];
const BOLT_BOX = new THREE.BoxGeometry(1, 1, 1); // shared unit box, scaled per segment

function spawnBolt(player: THREE.Vector3): void {
  if (!scene || bolts.length >= 2) return;
  const az = Math.random() * Math.PI * 2;
  const dist = 60 + Math.random() * 60; // middle distance, half-veiled by storm fog
  const bx = player.x + Math.sin(az) * dist;
  const bz = player.z + Math.cos(az) * dist;
  const top = 85;
  const ground = heightAt(bx, bz);
  const mat = new THREE.MeshBasicMaterial({ // unlit white → blooms hard
    color: 0xffffff, transparent: true, opacity: 1, fog: false,
  });
  const group = new THREE.Group();
  const segs = 7 + Math.floor(Math.random() * 3);
  const a = new THREE.Vector3(bx, top, bz);
  const b = new THREE.Vector3();
  const dir = new THREE.Vector3();
  for (let i = 1; i <= segs; i++) {
    b.set(
      bx + (Math.random() * 2 - 1) * 4,
      top + (ground - top) * (i / segs),
      bz + (Math.random() * 2 - 1) * 4,
    );
    dir.subVectors(b, a);
    const len = dir.length();
    const seg = new THREE.Mesh(BOLT_BOX, mat);
    seg.position.copy(a).addScaledVector(dir, 0.5);
    seg.quaternion.setFromUnitVectors(_axisY, dir.normalize());
    seg.scale.set(0.22, len, 0.22);
    group.add(seg);
    a.copy(b);
  }
  scene.add(group);
  bolts.push({ group, mat, life: BOLT_LIFE });
}

function updateLightning(dt: number, player: THREE.Vector3): void {
  // Age out bolts (fade over their short life, then dispose the material —
  // the box geometry is shared and lives forever).
  for (let i = bolts.length - 1; i >= 0; i--) {
    const bl = bolts[i];
    bl.life -= dt;
    if (bl.life <= 0) {
      scene?.remove(bl.group);
      bl.mat.dispose();
      bolts.splice(i, 1);
    } else {
      bl.mat.opacity = bl.life / BOLT_LIFE;
    }
  }
  flashT = Math.max(0, flashT - dt);
  const s = effStorm();
  if (s <= 0.01) { nextFlash = 0; return; }
  if (nextFlash <= 0) nextFlash = 2 + (Math.random() * 18) / s; // storm just started
  nextFlash -= dt;
  if (nextFlash <= 0) {
    flashT = FLASH_DUR;
    nextFlash = 2 + (Math.random() * 18) / s; // heavier storm → tighter cadence
    if (Math.random() < 0.3) spawnBolt(player);
  }
}

/** Place + phase + fade the moon disc (opposite the sun, following the player
 *  like the sun does so it never leaves the far plane). Rides `dn.sunDir` —
 *  the direction daynight already computed this frame — negated. */
function updateMoon(player: THREE.Vector3, night: number): void {
  if (!moon || !dn) return;
  const phase = moonPhase(day);
  const mat = moon.material as THREE.SpriteMaterial;
  mat.map = moonTexture(phase); // memoized per phase — free to assign each frame
  moon.position.copy(player).addScaledVector(_dir.copy(dn.sunDir).negate(), MOON_DIST);
  mat.opacity = night * (1 - 0.85 * cur.cloud);
  moon.visible = phase !== 0 && mat.opacity > 0.02;
}

// ---- Per-frame hook -------------------------------------------------------------
// Called from game.ts immediately after `dayNight.update` (see the ordering
// note on gradeAtmosphere). `night` is daynight's return (1 midnight, 0 noon).

export function updateWeather(
  dt: number, t: number, player: THREE.Vector3, cam: THREE.Vector3, night: number,
): void {
  stepEase(dt);
  updateLightning(dt, player); // before the grade — it reads the flash envelope
  gradeAtmosphere(night);
  updateMoon(player, night);
  updatePrecip(t, player, cam);
  updateClouds(dt, player);
  updateRainAudio();
}

/** Drive the ambience mixer's rain beds from the rendered weather state.
 *  Light↔heavy crossfade on precip intensity; mute indoors (playerShelter);
 *  storm boosts heavy; heavy rain ducks map base/zones/emitters. */
function updateRainAudio(): void {
  const rainAmt = cur.kind === "rain" ? effPrecip() : 0;
  // Shelter is 1-frame stale (same as roof-clip visuals) — FADE_TAU eases the
  // gain so walking through a doorway never clicks the bed on/off.
  const shelterMul = playerShelter() ? 0 : 1;
  const storm = effStorm();
  const a = RAIN_AUDIO;
  const light =
    rainAmt * (1 - smoothstep(a.lightOutStart, a.lightOutEnd, rainAmt));
  const heavy =
    rainAmt * smoothstep(a.heavyInStart, a.heavyInEnd, rainAmt);
  const lightOut = Math.min(1, light * shelterMul * (1 + a.stormLightBoost * storm));
  const heavyOut = Math.min(1, heavy * shelterMul * (1 + a.stormHeavyBoost * storm));
  const ambienceDuck =
    shelterMul *
    Math.min(1, smoothstep(a.duckStart, 1, rainAmt) * (a.duckMax + a.duckStormExtra * storm));
  setWeatherAmbience({ light: lightOut, heavy: heavyOut, ambienceDuck });
}
